import * as yup from 'yup';

/**
 * Yup validation schema for Sign Up form
 */
export const signUpValidationSchema = yup.object().shape({
  firstName: yup
    .string()
    .required('First name is required')
    .test('is-valid-name', 'First name should only contain letters', value => {
      if (!value) return false;
      return /^[a-zA-Z\s\u00C0-\u017F]+$/.test(value.trim());
    }),

  lastName: yup
    .string()
    .required('Last name is required')
    .test('is-valid-name', 'Last name should only contain letters', value => {
      if (!value) return false;
      return /^[a-zA-Z\s\u00C0-\u017F]+$/.test(value.trim());
    }),

  username: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address (e.g., name@example.com)')
    .test('is-valid-email', 'Please enter a valid email address (e.g., name@example.com)', value => {
      if (!value) return false;
      const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(value.trim());
    }),

  organizationName: yup
    .string()
    .required('Organization name is required')
    .min(2, 'Organization name must be at least 2 characters long')
    .test('has-letter', 'Organization name must contain at least one letter', value => {
      if (!value) return false;
      return /[a-zA-Z]/.test(value.trim());
    })
    .test('only-letters-spaces', 'Organization name can only contain letters and spaces', value => {
      if (!value) return false;
      return /^[a-zA-Z\s]+$/.test(value.trim());
    })
    .test('starts-ends-letter', 'Organization name must start and end with a letter', value => {
      if (!value) return false;
      const trimmed = value.trim();
      return /^[a-zA-Z]/.test(trimmed) && /[a-zA-Z]$/.test(trimmed);
    }),

  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters long')
    .max(64, 'Password must not exceed 64 characters'),

  confirmPassword: yup
    .string()
    .required('Confirm Password is required')
    .oneOf([yup.ref('password')], 'Passwords do not match'),

  domain: yup.string().required('Domain is required'),

  acceptedTerms: yup.boolean().oneOf([true], 'You must accept the terms and conditions to continue')
});

/**
 * Filters invalid characters from name input - only allows letters and spaces
 */
export const filterNameInput = (value: string): string => {
  return value.replace(/[^a-zA-Z\s\u00C0-\u017F]/g, '');
};

/**
 * Filters invalid characters from email input
 */
export const filterEmailInput = (value: string): string => {
  const parts = value.split('@');

  if (parts.length > 2) {
    const localPart = parts[0].replace(/[^a-zA-Z0-9._+-]/g, '');
    const domainPart = parts
      .slice(1)
      .join('@')
      .replace(/[^a-zA-Z0-9.-]/g, '');
    return localPart + '@' + domainPart;
  } else if (parts.length === 2) {
    const localPart = parts[0].replace(/[^a-zA-Z0-9._+-]/g, '');
    const domainPart = parts[1].replace(/[^a-zA-Z0-9.-]/g, '');
    return localPart + '@' + domainPart;
  } else {
    return value.replace(/[^a-zA-Z0-9._+-]/g, '');
  }
};

/**
 * Filters invalid characters from organization name input - only letters and spaces
 */
export const filterOrganizationNameInput = (value: string): string => {
  return value.replace(/[^a-zA-Z\s]/g, '');
};
